var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useCallback, useMemo } from 'react';
import { useDelete } from "../../dataProvider/useDelete.js";
import { useUnselect } from "../list/useUnselect.js";
import { useRecordContext } from "../record/useRecordContext.js";
import { useRedirect } from "../../routing/useRedirect.js";
import { useNotify } from "../../notification/useNotify.js";
import { useResourceContext } from "../../core/useResourceContext.js";
import { useTranslate } from "../../i18n/useTranslate.js";
/**
 * Prepare a set of callbacks for a delete button
 *
 * @example
 * const DeleteButton = ({
 *     redirect,
 *     ...rest
 * }) => {
 *     const {
 *         isPending,
 *         handleDelete,
 *     } = useDeleteController({
 *         mutationMode: 'pessimistic',
 *         redirect,
 *     });
 *
 *     const [open, setOpen] = useState(false);
 *
 *     return (
 *         <Fragment>
 *             <Button
 *                 onClick={() => setOpen(true)}
 *                 label="ra.action.delete"
 *                 {...rest}
 *             >
 *                 {icon}
 *             </Button>
 *             <Confirm
 *                 isOpen={open}
 *                 loading={isPending}
 *                 title="ra.message.delete_title"
 *                 content="ra.message.delete_content"
 *                 titleTranslateOptions={{
 *                     name: resource,
 *                     id: record.id,
 *                 }}
 *                 contentTranslateOptions={{
 *                     name: resource,
 *                     id: record.id,
 *                 }}
 *                 onConfirm={() => handleDelete()}
 *                 onClose={() => setOpen(false)}
 *             />
 *         </Fragment>
 *     );
 * };
 */
export var useDeleteController = function (props) {
    var _a = props.redirect, redirectTo = _a === void 0 ? 'list' : _a, _b = props.mutationMode, mutationMode = _b === void 0 ? 'undoable' : _b, _c = props.mutationOptions, mutationOptions = _c === void 0 ? {} : _c, successMessage = props.successMessage;
    var mutationMeta = mutationOptions.meta, otherMutationOptions = __rest(mutationOptions, ["meta"]);
    var record = useRecordContext(props);
    var resource = useResourceContext(props);
    var notify = useNotify();
    var unselect = useUnselect(resource);
    var redirect = useRedirect();
    var translate = useTranslate();
    var _d = useDelete(resource, undefined, {
        onSuccess: function () {
            notify(successMessage !== null && successMessage !== void 0 ? successMessage : "resources.".concat(resource, ".notifications.deleted"), {
                type: 'info',
                messageArgs: {
                    smart_count: 1,
                    _: translate('ra.notification.deleted', {
                        smart_count: 1,
                    }),
                },
                undoable: mutationMode === 'undoable',
            });
            record && unselect([record.id], true);
            redirect(redirectTo, resource);
        },
        onError: function (error) {
            notify(typeof error === 'string'
                ? error
                : (error === null || error === void 0 ? void 0 : error.message) || 'ra.notification.http_error', {
                type: 'error',
                messageArgs: {
                    _: typeof error === 'string'
                        ? error
                        : error === null || error === void 0 ? void 0 : error.message,
                },
            });
        },
    }), deleteOne = _d[0], isPending = _d[1].isPending;
    var handleDelete = useCallback(function () {
        if (!record) {
            throw new Error('The record cannot be deleted because no record has been passed');
        }
        deleteOne(resource, {
            id: record.id,
            previousData: record,
            meta: mutationMeta,
        }, __assign({ mutationMode: mutationMode }, otherMutationOptions));
    }, [
        deleteOne,
        mutationMeta,
        mutationMode,
        otherMutationOptions,
        record,
        resource,
    ]);
    return useMemo(function () { return ({
        isPending: isPending,
        isLoading: isPending,
        handleDelete: handleDelete,
    }); }, [isPending, handleDelete]);
};
//# sourceMappingURL=useDeleteController.js.map